import './AdListItem.scss';

import { IconAlertWarningYellow, IconDelete } from '@hulu-react-style-components/icons';
import type { SyntheticEvent } from 'react';
import React, { useLayoutEffect, useMemo, useRef } from 'react';

import { getAssetIds } from '../../../../common/AdForm/utils';
import DetailsTable from '../../../../common/DetailsTable';
import { formatCountryIdsAsNames, useCountryNames } from '../../../../common/hooks/useCountryNames';
import type { PillProps } from '../../../../common/Pill';
import Pill from '../../../../common/Pill';
import PreviewLinkButton from '../../../../common/PreviewLinkButton';
import bem from '../../../../utils/bem';
import { isDxcCreativeType } from '../../../../utils/creative';
import { formatTimeDetail } from '../../../../utils/formatting';
import { getPodPositionLabel, getSlotPositionLabel } from '../../../Trafficking/TraffickingPage/columns/utils';
import type { AdListOptionalFormState } from '../types';
import { getStatusPills } from '../utils';
import { useAssetIdsForTranscodeStatus } from './hooks/useAssetIdsForTranscodeStatus';

const unsavedChangesMessage = (
  <span className="unsaved-changes">
    <IconAlertWarningYellow />
    <span> Unsaved changes!</span>
  </span>
);

const [block, element] = bem('ad-list-item');

export interface AdListItemProps {
  readonly weight: number;
  readonly adListElement: AdListOptionalFormState;
  readonly isSelected: boolean;
  readonly isSaved: boolean;
  readonly selectedFocusRef?: (el: HTMLLIElement) => HTMLLIElement | null;
  readonly handleItemClick?: (e: SyntheticEvent) => void;
  readonly handleDeleteClick?: (e: SyntheticEvent) => void;
  readonly handleRemoveAd?: (e: React.KeyboardEvent<HTMLSpanElement>) => void;
  readonly handleSelectAd?: (e: React.KeyboardEvent<HTMLLIElement>) => void;
  readonly isAdListWithOneItem?: boolean;
  readonly isSequence: boolean;
  readonly onContentChange?: () => void;
}

function AdListItem({
  adListElement,
  isSelected,
  handleItemClick,
  handleDeleteClick,
  handleRemoveAd,
  handleSelectAd,
  isSaved,
  selectedFocusRef,
  weight,
  isAdListWithOneItem,
  isSequence,
  onContentChange,
}: AdListItemProps): JSX.Element {
  const { countryMap } = useCountryNames();
  const ad = adListElement.ad;
  const assetIdsStr = getAssetIds(ad?.creative).join(', ');
  const {
    transcodePillColor,
    transcodeStatus,
    assetTagList,
    review,
    allowedPublishersText,
    approvedCountries,
  } = useAssetIdsForTranscodeStatus({
    creative: ad?.creative,
    publisherList: ad?.lineItem?.displayPublisherTarget || [],
    lineItemCountries: ad?.lineItem?.countries,
    targetingTermValues: ad?.targetingTermValues,
  });

  const createdAt: string | undefined = ad.creative?.createdAt?.toString();
  const creativeCreatedAt = createdAt ? formatTimeDetail(createdAt) : '';

  const approvedCountriesText = useMemo(() => {
    if (!approvedCountries?.length) {
      return null;
    }

    return formatCountryIdsAsNames(approvedCountries, countryMap);
  }, [approvedCountries, countryMap]);

  const pillsList = useMemo(() => {
    return getStatusPills({
      adListElement,
      assetIdsStr,
      review,
      transcodeStatus,
      transcodePillColor,
    });
  }, [adListElement, assetIdsStr, review, transcodeStatus, transcodePillColor]);

  const assetDetailList = useMemo((): [string, React.JSX.Element][] => {
    const list: [string, React.JSX.Element][] = [
      ['Ad ID', <>{ad.id}</>],
      ['Created', <>{formatTimeDetail(ad.createdAt)}</>],
      ['Creative ID', <>{ad.creative?.id ?? ''}</>],
      ['Creative Created', <>{creativeCreatedAt}</>],
      [
        'Saved',
        <>
          {ad.updatedAt && formatTimeDetail(ad.updatedAt)} {ad.dirty && unsavedChangesMessage}
        </>,
      ],
    ];

    if (assetTagList) {
      list.push(['Asset Tags', <>{assetTagList}</>]);
    }

    if (allowedPublishersText) {
      list.push(['Approved Publishers', <>{allowedPublishersText}</>]);
    }

    if (approvedCountriesText) {
      list.push(['Approved Countries', <>{approvedCountriesText}</>]);
    }

    if (ad.creative && isDxcCreativeType(ad.creative.type) && ad.creative.experienceId) {
      list.push([
        'DXC Preview Link',
        <PreviewLinkButton key={ad.creative.experienceId} experienceId={ad.creative.experienceId} />,
      ]);
    }

    if (ad.adSequence) {
      const podLabel = getPodPositionLabel(ad.adSequence.podPosition);
      const slotLabel = getSlotPositionLabel(ad.adSequence.podAlignment);
      if (podLabel) list.push(['Pod Position', <>{podLabel}</>]);
      if (slotLabel) list.push(['Slot Position', <>{slotLabel}</>]);
    }

    return list;
  }, [
    ad.createdAt,
    ad.creative,
    ad.dirty,
    ad.id,
    ad.updatedAt,
    ad.adSequence,
    allowedPublishersText,
    approvedCountriesText,
    assetTagList,
    creativeCreatedAt,
  ]);

  const isFirstRender = useRef(true);
  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onContentChange?.();
  }, [transcodeStatus, review, assetTagList, allowedPublishersText, approvedCountriesText, onContentChange]);

  const detailsTable = <DetailsTable details={assetDetailList} />;

  const newAdDetailsTable = (
    <DetailsTable
      details={[
        [
          null,
          <>
            {ad.updatedAt && formatTimeDetail(ad.updatedAt)}{' '}
            {ad.dirty && (
              <span className="unsaved-changes">
                <IconAlertWarningYellow />
                <span> Unsaved changes!</span>
              </span>
            )}
          </>,
        ],
      ]}
    />
  );

  return (
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    <li
      ref={selectedFocusRef}
      tabIndex={0}
      className={block(isSelected ? 'selected' : '')}
      aria-selected={isSelected}
      onClick={handleItemClick}
      onKeyDown={handleSelectAd}
      role="option"
    >
      <span className={element('percentage')} data-testid="sequence-rotation-order">
        {isSequence || !ad.id ? ad?.adSequence?.order : `${weight}%`}
      </span>

      <div className={element('content-container')}>
        <header>
          <h1 className={element('name', ad.name ? '' : 'no-ad')}>{ad.name || 'Untitled Ad'}</h1>
          {isSelected && !isSaved && !isAdListWithOneItem && (
            <div className={element('actions')}>
              <span
                tabIndex={0}
                role="button"
                className={element('action')}
                onClick={handleDeleteClick}
                onKeyDown={handleRemoveAd}
                data-testid="delete-btn"
              >
                <IconDelete aria-label="delete ad" />
              </span>
            </div>
          )}
        </header>
        {isSaved ? detailsTable : newAdDetailsTable}
        <div className={element('pills')} data-testid="status-pills">
          {pillsList.map(
            (pill: PillProps): JSX.Element => (
              <Pill key={`${pill.text}-${ad.id}`} {...pill} />
            )
          )}
        </div>
      </div>
    </li>
  );
}

export default AdListItem;
